import {ResearchDataFilter, ResearchDataset, ResearchWidgetKind, ResearchWidgetState} from '../../../data/ResearchTypes';
import {ResearchPreset} from '../ResearchPresets';

/**
 * DashboardSpec - a research dashboard as plain JSON: its widgets, where they sit, and each one's
 * saved state (data source plus GridSpec / ChartSpec). It is the canvas state in the shape the AI
 * dashboard assistant changes, through DashboardOps (see DashboardSpecOps.ts) applied by one reducer.
 *
 * The assistant does not read or write a widget's full spec. It works with a WidgetBrief - the data
 * and the fields shown - and the reducer turns a brief into a spec with the same generators a widget
 * uses for its default. The look of one widget is refined by that widget's own assistant.
 *
 * NOTE: the assistant's output schema, dashboardSpecOps.schema.json, describes the brief and the ops; the
 * client sends it with each request (rzx-backend/docs/decisions/0027, 0037). Keep it in step with any
 * change here, and bump DASHBOARD_SPEC_VERSION on a breaking one.
 */
export const DASHBOARD_SPEC_VERSION = 1;

/** Position and size on the canvas, in columns and rows. */
export interface DashboardWidgetLayout {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface DashboardWidget {
    /** The canvas's id of the widget - or a temporary one for a widget the canvas doesn't hold yet. */
    id: string;
    kind: ResearchWidgetKind;
    title: string;
    layout: DashboardWidgetLayout;
    state: ResearchWidgetState;
}

export interface DashboardSpec {
    version: number;
    widgets: DashboardWidget[];
}

/** What the reducer validates against - the canvas, the datasets a widget can show, the ready-made widgets. */
export interface DashboardCapabilities {
    columns: number;
    /** Height of a canvas row in pixels. */
    rowHeight: number;
    datasets: ResearchDataset[];
    presets: ResearchPreset[];
}

/** Chart types the assistant picks from - the rest are set in the chart's own configurator. */
export const BRIEF_CHART_TYPES = ['line', 'area', 'column', 'bar', 'pie'] as const;
export type BriefChartType = (typeof BRIEF_CHART_TYPES)[number];

export const WIDGET_KINDS: ResearchWidgetKind[] = ['grid', 'chart'];

/** Smallest and largest size of a widget, in columns and rows. */
export const MIN_WIDGET_SIZE = 4;
export const MAX_WIDGET_HEIGHT = 40;

/** What a widget shows, as the assistant reads and writes it. */
export interface WidgetBrief {
    kind: ResearchWidgetKind;
    title?: string;
    dataset: string;
    /** Only the newest snapshot. */
    latest?: boolean;
    /** Grids only - the newest row, one line per field. */
    transpose?: boolean;
    filters?: ResearchDataFilter[];
    /** A grid's columns, a transposed grid's lines, or the measures a chart plots - in order. */
    fields?: string[];
    /** Charts only. */
    chartType?: BriefChartType;
    /** What the widget is for, in a line - the widget's own assistant refines it to that. */
    purpose?: string;
}

/** A widget of the dashboard as sent to the assistant - its brief, id and place. */
export interface DescribedWidget extends Partial<WidgetBrief>, DashboardWidgetLayout {
    id: string;
    kind: ResearchWidgetKind;
    title: string;
}

/** A ready-made widget as sent to the assistant. */
export interface DescribedPreset {
    id: string;
    title: string;
    kind: ResearchWidgetKind;
    dataset: string;
    w: number;
    h: number;
}
